import { motion, type HTMLMotionProps, type Variants } from "motion/react";
import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Shared table primitives. Matches the landing-page challenges-table
 * visual: `border-b-2 border-accent/20` header, motion.tr stagger,
 * hover fill, center-aligned numerics. Every dashboard table ships
 * through these so the pattern stays identical across pages.
 *
 * Use as a low-level shell — callers keep full control over columns,
 * cell rendering, and filter/pagination UI, they just wrap in these
 * building blocks.
 */

export const tableRowVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      delay: i * 0.03,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

export function DataTableShell({
  className,
  minWidth = 900,
  children,
}: {
  className?: string;
  /** min-width applied to the inner `<table>` so columns don't crush
   *  before the caller's responsive breakpoints kick in. Set a number
   *  matching the sum of your content widths. */
  minWidth?: number;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={cn("hidden overflow-x-auto lg:block", className)}>
      <table
        className="w-full border-collapse"
        style={{ minWidth: `${minWidth}px` }}
      >
        {children}
      </table>
    </div>
  );
}

export function DataTableHeader({
  labels,
}: {
  /** First label renders left-aligned; all subsequent labels render
   *  center-aligned — mirrors the challenges-table convention where
   *  col 1 is the "primary entity" and the rest are scannable metrics. */
  labels: readonly string[];
}): ReactNode {
  return (
    <thead>
      <tr className="border-b-2 border-accent/20">
        {labels.map((label, idx) => (
          <th
            key={label}
            className={cn(
              "px-4 py-3 text-[11px] font-medium tracking-wide text-foreground/50",
              idx === 0 ? "text-left" : "text-center",
            )}
          >
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

interface DataTableRowProps extends Omit<HTMLMotionProps<"tr">, "ref"> {
  index: number;
  muted?: boolean;
}

/**
 * Body row with stagger-in animation + hover fill + standard border.
 * Pass `muted` for archived/terminal rows (renders at 70% opacity).
 */
export function DataTableRow({
  index,
  muted,
  className,
  children,
  ...rest
}: DataTableRowProps): ReactNode {
  return (
    <motion.tr
      custom={index}
      variants={tableRowVariants}
      initial="hidden"
      animate="visible"
      whileHover={{
        backgroundColor: "var(--color-muted)",
        transition: { duration: 0.15 },
      }}
      className={cn(
        "border-b border-border/50",
        muted && "opacity-70",
        className,
      )}
      {...rest}
    >
      {children}
    </motion.tr>
  );
}

/** Body cell — thin wrapper that applies the canonical cell padding +
 *  center/left alignment pattern. `align` defaults to center (numeric). */
export function DataTableCell({
  className,
  align = "center",
  children,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & {
  align?: "left" | "center" | "right";
}): ReactNode {
  return (
    <td
      className={cn(
        "whitespace-nowrap px-3 py-4 align-middle",
        align === "left" && "text-left",
        align === "center" && "text-center",
        align === "right" && "text-right",
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

/** Standalone head-cell for callers that need custom alignment per
 *  column (instead of the positional convention DataTableHeader uses). */
export function DataTableHead({
  className,
  align = "center",
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & {
  align?: "left" | "center" | "right";
}): ReactNode {
  return (
    <th
      className={cn(
        "px-4 py-3 text-[11px] font-medium tracking-wide text-foreground/50",
        align === "left" && "text-left",
        align === "center" && "text-center",
        align === "right" && "text-right",
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}
